<script setup>
import { router } from '@inertiajs/vue3';
import ManageMediasAction from '@js/components/actions/ManageMediasAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { number, object, string } from 'yup';

const { tenant, configuration } = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    configuration: {
        required: false,
        type: Object,
    },
});

const transactionEmailApis = [
    { value: 'mailjet', label: 'Mailjet' },
    { value: 'amazonSes', label: 'Amazon SES' },
];

const { route } = useRoute();

const isSubmitting = ref(false);

const form = reactive({
    transactionEmailApi:
        transactionEmailApis.find(
            (api) => api.value === configuration?.data.transactionEmailApi,
        ) ?? null,
    internalNotes: configuration ? configuration.data.internalNotes : null,
    defaultSenderName: configuration
        ? configuration.data.defaultSenderName
        : '',
    defaultSenderEmail: configuration
        ? configuration.data.defaultSenderEmail
        : '',
    defaultReplyToName: configuration
        ? configuration.data.defaultReplyToName
        : '',
    defaultReplyToEmail: configuration
        ? configuration.data.defaultReplyToEmail
        : '',
    numberOfDaysForTracking:
        configuration && configuration.data.numberOfDaysForTracking
            ? configuration.data.numberOfDaysForTracking
            : 7,
});

const validationSchema = object({
    transactionEmailApi: object().required(
        "L'API d'envoi des emails transactionnels est requise",
    ),
    internalNotes: string().nullable(),
    defaultSenderName: string().nullable(),
    defaultSenderEmail: string()
        .email("Le format de l'email de l'expéditeur par défaut est invalide")
        .nullable(),
    defaultReplyToName: string().nullable(),
    defaultReplyToEmail: string()
        .email("Le format de l'email de la réponse par défaut est invalide")
        .nullable(),
    numberOfDaysForTracking: number()
        .min(
            7,
            'Le nombre de jours pour le suivi des emails doit être au moins de 7',
        )
        .integer(
            'Le nombre de jours pour le suivi des emails doit être un entier',
        )
        .required('Le nombre de jours pour le suivi des emails est requis'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.modules.postEmailingConfiguration', {
                tenant: tenant.id,
            }),
            {
                transactionEmailApi: form.transactionEmailApi.value,
                internalNotes: form.internalNotes,
                defaultSenderName: form.defaultSenderName,
                defaultSenderEmail: form.defaultSenderEmail,
                defaultReplyToName: form.defaultReplyToName,
                defaultReplyToEmail: form.defaultReplyToEmail,
                numberOfDaysForTracking: form.numberOfDaysForTracking,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.modules.emailings', {
                    tenant: tenant.id,
                    tab: 'configuration',
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.modules.emailings', {
                    tenant: tenant.id,
                }),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <ManageMediasAction
                    :path="'tenant/' + tenant.id + '/emailings/media'"
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Gérer les médias de l'emailing
                        </ActionButton>
                    </template>
                </ManageMediasAction>
            </FormContent>

            <FormContent>
                <ListboxField
                    v-model="form.transactionEmailApi"
                    field-id="transactionEmailApi"
                    field-name="transactionEmailApi"
                    label="API d'envoi des emails transactionnels"
                    :options="transactionEmailApis"
                    required
                >
                    <template #buttonContent>
                        {{ form.transactionEmailApi.label }}
                    </template>

                    <template #optionContent="{ option }">
                        {{ option.label }}
                    </template>
                </ListboxField>
            </FormContent>

            <FormContent>
                <EditorField
                    v-model="form.internalNotes"
                    label="Notes internes"
                    field-name="internalNotes"
                    field-id="internalNotes"
                    size="small"
                />
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <InputField
                        v-model="form.defaultSenderName"
                        label="Nom de l'expéditeur par défaut"
                        field-name="defaultSenderName"
                        field-id="defaultSenderName"
                    />

                    <InputField
                        v-model="form.defaultSenderEmail"
                        label="Email de l'expéditeur par défaut"
                        field-name="defaultSenderEmail"
                        field-id="defaultSenderEmail"
                        type="email"
                    />

                    <InputField
                        v-model="form.defaultReplyToName"
                        label="Nom de la réponse par défaut"
                        field-name="defaultReplyToName"
                        field-id="defaultReplyToName"
                    />

                    <InputField
                        v-model="form.defaultReplyToEmail"
                        label="Email de la réponse par défaut"
                        field-name="defaultReplyToEmail"
                        field-id="defaultReplyToEmail"
                        type="email"
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid>
                    <InputField
                        v-model="form.numberOfDaysForTracking"
                        label="Nombre de jours pour le suivi des emails"
                        field-name="numberOfDaysForTracking"
                        field-id="numberOfDaysForTracking"
                        type="number"
                        min="7"
                        step="1"
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors" />
    </FormRoot>
</template>
